import deepmerge from 'deepmerge'
import cloneDeep from 'lodash/cloneDeep'
import difference from 'lodash/difference'
import includes from 'lodash/includes'
import intersection from 'lodash/intersection'
import isEqual from 'lodash/isEqual'
import isObject from 'lodash/isObject'
import isPlainObject from 'lodash/isPlainObject'
import union from 'lodash/union'
import type { IObservableArray } from 'mobx'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'

import apiClient from './apiClient'
import type {
  CreateOptions,
  DestroyOptions,
  FetchOptions,
  FindOptions,
  GetOptions,
  RestMethod,
  SaveOptions,
  SetOptions,
} from './types'

type IndexTree<M> = Map<string, Index<M>>
type Index<M> = Map<string | null, M[]>
interface Creator<M> extends HasStaticUrl {
  create: (args: Record<string, any>, options: CreateOptions) => Promise<M>
}

// TODO: Check if we can use generic instead of default any in Model
abstract class Collection<M extends Model> {
  models: IObservableArray<M>
  previousFetchDataParams: object | null | undefined = null

  constructor(data: Array<ModelAttributes<M>> = []) {
    this.models = observable.array(data.map((m) => this.build(m)))

    makeObservable(this, {
      index: computed({ keepAlive: true }),
      length: computed,
      isEmpty: computed,
      add: action,
      reset: action,
      remove: action,
      set: action,
      create: action,
      fetch: action,
    })
  }

  /*
   * Override this to have more indexes
   */
  get indexes(): string[] {
    return []
  }

  /**
   * Define which is the primary key
   * of the model's in the collection.
   *
   * FIXME: This contains a hack to use the `primaryKey` directly
   * from the prototype. Ideally it should be static but that
   * would not be backward compatible and Typescript sucks at
   * static polymorphism (https://github.com/microsoft/TypeScript/issues/5863).
   */
  get primaryKey(): string {
    const ModelClass = this.model()

    if (!ModelClass) return DEFAULT_PRIMARY

    return ModelClass.prototype.primaryKey
  }

  /**
   * Returns a hash with all the indexes for that
   * collection.
   *
   * We keep the indexes in memory for as long as the
   * collection is alive, even if no one is referencing it.
   * This way we can ensure to calculate it only once.
   */
  get index(): IndexTree<M> {
    const indexes = this.indexes.concat([this.primaryKey])

    return indexes.reduce((tree: IndexTree<M>, attr: string) => {
      const newIndex = this.models.reduce((index: Index<M>, model: M) => {
        const value = model.has(attr) ? model.attributes[attr] : null
        const oldModels = index.get(value) || []

        return index.set(value, oldModels.concat(model))
      }, new Map())

      return tree.set(attr, newIndex)
    }, new Map())
  }

  /**
   * Alias for models.length
   */
  get length() {
    return this.models.length
  }

  /**
   * Alias for models.map
   */
  map<P>(callback: (model: M) => P): P[] {
    return this.models.map(callback)
  }

  /**
   * Alias for models.forEach
   */
  forEach(callback: (model: M) => void): void {
    return this.models.forEach(callback)
  }

  /**
   * Specifies the model class for that collection
   */
  abstract model(): ModelConstructor<M> & Creator<M>

  /**
   * Returns a JSON representation
   * of the collection
   */
  toJS(): Array<{ [key: string]: any }> {
    return this.models.map((model) => model.toJS())
  }

  /**
   * Alias of slice
   */
  toArray(): M[] {
    return this.slice()
  }

  /**
   * Returns a defensive shallow array representation
   * of the collection
   */
  slice(): M[] {
    return this.models.slice()
  }

  /**
   * Wether the collection is empty
   */
  get isEmpty(): boolean {
    return this.length === 0
  }

  /**
   * Get a resource at a given position
   */
  at(index: number): M | undefined {
    return this.models[index]
  }

  /**
   * Get a resource with the given id or uuid
   */
  get(id: any, { required = false }: GetOptions = {}): M | undefined {
    const models = this.index.get(this.primaryKey)?.get(id)
    const model = models && models[0]

    if (!model && required) {
      throw new Error(
        `Invariant: ${this.model().name} must be found with ${this.primaryKey}: ${id}`
      )
    }

    return model
  }

  /**
   * Get resources matching criteria.
   *
   * If passing an object of key:value conditions, it will
   * use the indexes to efficiently retrieve the data.
   */
  filter(query: { [key: string]: any } | ((query: M) => boolean)): M[] {
    if (typeof query === 'function') {
      return this.models.filter((model) => query(model))
    }

    // Sort the query to hit the indexes with more cardinality first
    // This ensures that we first test for those indexes that filter more rows
    const optimizedQuery = Object.entries(query).sort(
      (A, B) => (this.index.get(B[0])?.size || 0) - (this.index.get(A[0])?.size || 0)
    )

    return (
      optimizedQuery.reduce((values: M[] | null, [attr, value]): M[] => {
        const attrIndex = this.index.get(attr)

        // Hitting index
        if (attrIndex) {
          const newValues = attrIndex.get(value)

          // The index says, no results
          if (!newValues) {
            return []
            // First pass, we take the results
          } else if (!values) {
            return newValues
            // Second pass, we intersect the new results with the previous
            // but only if we narrow it down, otherwise, scan the rest.
          } else if (newValues.length < values.length) {
            return intersection(values, newValues)
          }
        }

        // Scan the models, either no index was found, or it was not discriminating enough
        return (values || this.models).filter(
          (model: M) => model.has(attr) && model.get(attr) === value
        )
      }, null) ?? []
    )
  }

  /**
   * Finds an element with the given matcher
   */
  find(
    query: { [key: string]: any } | ((query: M) => boolean),
    { required = false }: FindOptions = {}
  ): M | undefined {
    const model =
      typeof query === 'function'
        ? this.models.find((model) => query(model))
        : this.filter(query)[0]

    if (!model && required) {
      throw new Error(`Invariant: ${this.model().name} must be found`)
    }

    return model
  }

  /**
   * Finds an element with the given matcher
   * @returns boolean
   */
  some(query: { [key: string]: any } | ((query: M) => boolean)): boolean {
    const model =
      typeof query === 'function'
        ? this.models.some((model) => query(model))
        : this.filter(query)[0]

    return !!model
  }

  /**
   * Returns the last element of the collection
   */
  last(): M | undefined {
    return this.models[this.models.length - 1]
  }

  /**
   * Adds a model or collection of models.
   */
  add(data: Array<ModelAttributes<M> | M> | ModelAttributes<M> | M): [M, ...Array<M>] {
    if (!Array.isArray(data)) data = [data]

    const models = difference(
      data.map((m) => this.build(m)),
      this.models
    )

    this.models.push(...models)

    // We know that the array is not empty, but TS needs some help
    // eslint-disable-next-line no-type-assertion/no-type-assertion
    return models as [M, ...Array<M>]
  }

  /**
   * Resets the collection of models.
   */
  reset(data: Array<ModelAttributes<M> | M>): void {
    this.models.replace(data.map((m) => this.build(m)))
  }

  /**
   * Removes the model with the given ids or uuids
   */
  remove(ids: any | M | Array<any | M>): void {
    if (!Array.isArray(ids)) {
      ids = [ids]
    }

    const toKeep: Set<M> = new Set(this.models)

    ids.forEach((id: any) => {
      let model: M | undefined

      if (id instanceof Model && id.collection === this) {
        model = id as M
      } else if (typeof id === 'number' || typeof id === 'string') {
        model = this.get(id)
      }

      if (!model) {
        return console.warn(
          `${this.constructor.name}: ${this.model().name} with ${this.primaryKey} ${id} not found.`
        )
      }

      toKeep.delete(model)
      model.collection = null
    })

    this.models.replace(Array.from(toKeep))
  }

  /**
   * Deletes the model from the backend and removes it from the collection
   */
  async delete(model: M, options: DestroyOptions = {}) {
    await model.delete(options)
    this.remove(model)
  }

  /**
   * Sets the resources into the collection.
   *
   * You can disable adding, changing or removing.
   */
  set(
    resources: Array<ModelAttributes<M>>,
    { add = true, change = true, remove = true }: SetOptions = {}
  ): void {
    const idsToRemove = new Set(this.index.get(this.primaryKey)?.keys())
    const resourcesToAdd = new Set<ModelAttributes<M>>([])

    idsToRemove.delete(null)

    resources.forEach((resource) => {
      const id = resource[this.primaryKey]
      const model = id ? this.get(id) : null

      if (!model) {
        resourcesToAdd.add(resource)
      } else {
        idsToRemove.delete(id)

        if (change) {
          model.set(resource)
        }
      }
    })

    if (remove && idsToRemove.size) {
      this.remove(Array.from(idsToRemove))
    }

    if (add && resourcesToAdd.size) {
      this.add(Array.from(resourcesToAdd))
    }
  }

  /**
   * Creates a new model instance with the given attributes
   */
  build(attributes: ModelAttributes<M> | M): M {
    if (attributes instanceof Model) {
      attributes.collection = this

      return attributes
    }

    const ModelClass = this.model()
    const model = new ModelClass(attributes)
    model.collection = this

    return model
  }

  /**
   * Creates the model and saves it on the backend
   */
  async create<T extends Record<string, any>>(
    attributes: T extends Model ? never : T,
    options: CreateOptions = {}
  ): Promise<ModelAttributes<M>> {
    const model = await this.model().create(attributes, options)

    this.add(model)

    // TODO: Evaluate if we should return the model instead of the attributes
    return model.toJS()
  }

  /**
   * Fetches the models from the backend.
   *
   * It uses `set` internally so you can
   * use the options to disable adding, changing
   * or removing.
   */
  async fetch<T = any>({
    data = {},
    path,
    withCachedFetchParams = false,
    ...otherOptions
  }: SetOptions = {}): Promise<T> {
    const client = apiClient()

    if (withCachedFetchParams) {
      const cachedData = client.cache.read<T>(client.cache.identify(this), data)

      if (cachedData) {
        return cachedData
      }
    }

    const newData = await client.get(path ?? this.model().staticUrl(), data, otherOptions)

    if (Array.isArray(newData)) this.set(newData, otherOptions)

    client.cache.write(client.cache.identify(this), data, this.toJS())

    return newData
  }

  /**
   * Call an RPC action for all those
   * non-REST endpoints that you may have in
   * your API.
   */
  rpc(endpoint: string | { rootUrl: string }, data?: {}, options?: {}): Promise<any> {
    const url = isObject(endpoint) ? endpoint.rootUrl : `${this.model().staticUrl()}/${endpoint}`

    return apiClient().post(url, data, options)
  }

  /**
   * In some cases we need to fetch data from a POST endpoint (until we migrate to GraphQL).
   */
  rpcWithSet(
    endpoint: string | { rootUrl: string },
    data?: object,
    options?: object
  ): Promise<any> {
    return this.rpc(endpoint, data, options).then((response) => {
      this.set(response)

      return response
    })
  }
}

const DEFAULT_PRIMARY = 'id'

export type ModelAttributes<M> = M extends Model<infer A> ? A : never

export type ModelConstructor<M> = new (attr: ModelAttributes<M>) => M

export interface HasStaticUrl {
  staticUrl: (id?: string) => string
}

class Model<Attributes extends Record<string, any> = any> {
  attributes: Attributes
  private readonly initialAttributes: Attributes
  committedAttributes: Attributes
  collection: Collection<this> | null = null

  constructor(attributes: Attributes) {
    this.attributes = { ...attributes }
    this.initialAttributes = { ...this.attributes }
    this.committedAttributes = { ...this.attributes }

    makeObservable(this, {
      attributes: observable,
      committedAttributes: observable,
      isNew: computed,
      changedAttributes: computed,
      _attributeSet: computed,
      changes: computed,
      commitChanges: action,
      discardChanges: action,
      reset: action,
      set: action,
      fetch: action,
      save: action,
      update: action,
      delete: action,
      destroy: action,
    })
  }

  /**
   * Returns a new object that represents the model data
   */
  toJS() {
    return cloneDeep<Attributes>(this.attributes)
  }

  /**
   * Define which is the primary
   * key of the model.
   *
   * @abstract
   */
  get primaryKey(): string {
    return DEFAULT_PRIMARY
  }

  /**
   * Return the base url used in
   * the `url` method
   *
   * @abstract
   */
  static urlRoot(): string {
    throw new Error('implement `urlRoot` method')
  }

  // TODO: rename this to url someday
  static staticUrl(id?: string): string {
    const urlRoot = this.urlRoot()

    return id ? `${urlRoot}/${id}` : urlRoot
  }

  /**
   * Return the url for this given REST resource
   *
   * @deprecated
   */
  url(): string {
    // NOTE: It looks like `this.constructor` has an invalid type
    // Check https://github.com/Microsoft/TypeScript/issues/3841 to
    // know more about it.
    const urlRoot = (this.constructor as any).staticUrl()

    if (this.isNew) {
      return urlRoot
    } else {
      return `${urlRoot}/${this.attributes[this.primaryKey]}`
    }
  }

  /**
   * Wether the resource is new or not
   *
   * We determine this asking if it contains
   * the `primaryKey` attribute (set by the server).
   *
   * @deprecated
   */
  get isNew(): boolean {
    return !this.has(this.primaryKey) || !this.attributes[this.primaryKey]
  }

  /**
   * Get the attribute from the model.
   *
   * Since we want to be sure changes on
   * the schema don't fail silently we
   * throw an error if the field does not
   * exist.
   *
   * If you want to deal with flexible schemas
   * use `has` to check wether the field
   * exists.
   */
  get<Attribute extends keyof Attributes & string>(attribute: Attribute) {
    if (this.has(attribute)) {
      return this.attributes[attribute]
    }

    throw new Error(`Attribute "${attribute}" not found`)
  }

  /**
   * Returns whether the given field exists
   * for the model.
   */
  has<Attribute extends keyof Attributes & string>(attribute: Attribute) {
    return this._attributeSet.has(attribute)
  }

  // TODO: Use `hasOwn` once it's available in Typescript (https://github.com/microsoft/TypeScript/issues/44253)
  get _attributeSet() {
    return new Set(Object.keys(this.attributes))
  }

  /**
   * Get an id from the model. It will use either
   * the backend assigned one or the client.
   */
  get id() {
    return this.attributes[this.primaryKey]
  }

  /**
   * Get an array with the attributes names that have changed.
   */
  get changedAttributes() {
    return getChangedAttributesBetween(this.committedAttributes, this.attributes)
  }

  /**
   * Gets the current changes.
   */
  get changes() {
    return getChangesBetween(this.committedAttributes, this.attributes)
  }

  /**
   * If an attribute is specified, returns true if it has changes.
   * If no attribute is specified, returns true if any attribute has changes.
   */
  hasChanges(attribute?: string): boolean {
    if (attribute) {
      return includes(this.changedAttributes, attribute)
    }

    return this.changedAttributes.length > 0
  }

  commitChanges(): void {
    replaceAttributes(this.committedAttributes, this.attributes)
  }

  discardChanges(): void {
    replaceAttributes(this.attributes, this.committedAttributes)
  }

  /**
   * Replace all attributes with new data
   */
  reset(data: Partial<Attributes> = {}): void {
    replaceAttributes(this.attributes, { ...this.initialAttributes, ...data })
  }

  /**
   * Merge the given attributes with
   * the current ones
   */
  set(data: {}): void {
    Object.assign(this.attributes, data)
  }

  /**
   * Fetches the model from the backend.
   */
  static async fetch<M>(
    this: ModelConstructor<M> & HasStaticUrl,
    { data, id, path, ...otherOptions }: FetchOptions = {}
  ) {
    const newData = await apiClient().get(path || this.staticUrl(id), data, otherOptions)

    return new this(newData)
  }

  /**
   * Fetches the model from the backend.
   *
   * @deprecated
   */
  async fetch({ data, path, ...otherOptions }: FetchOptions = {}) {
    const newData = await apiClient().get(path || this.url(), data, otherOptions)

    if (!newData) return

    action('fetch done', () => {
      this.set(newData)
      this.commitChanges()
    })()

    return newData
  }

  /**
   * Creates the resource on the backend.
   */
  static async create<M, A = ModelAttributes<M>>(
    this: ModelConstructor<M> & HasStaticUrl,
    attributes: Partial<A>,
    options: CreateOptions = {}
  ) {
    const { path } = options

    const newData = await apiClient().post(path || this.staticUrl(), attributes)

    return new this(newData)
  }

  /**
   *  Saves the resource on the backend.
   */
  async update(
    attributes?: Partial<Attributes> | null,
    { patch = true, keepChanges = false, path, ...otherOptions }: SaveOptions = {}
  ) {
    const currentAttributes = cloneDeep(this.attributes)
    let data: {}

    if (patch && attributes) {
      data = attributes
    } else if (patch) {
      data = this.changes
    } else {
      data = { ...currentAttributes, ...attributes }
    }

    const method: RestMethod = patch ? 'patch' : 'put'

    const newData = await apiClient()[method](
      path || (this.constructor as any).staticUrl(this.id),
      data,
      otherOptions
    )

    if (!newData) return

    const changes = getChangesBetween(currentAttributes, this.attributes)

    this.set(newData)
    this.commitChanges()

    if (keepChanges) {
      this.set(applyPatchChanges(newData, changes))
    }
  }

  /**
   * Saves the resource on the backend.
   *
   * If the item has a `primaryKey` it updates it,
   * otherwise it creates the new resource.
   *
   * @deprecated Use static create or update
   */
  async save(
    attributes?: {} | null,
    { patch = true, keepChanges = false, path, ...otherOptions }: SaveOptions = {}
  ) {
    const currentAttributes = cloneDeep(this.attributes)
    const collection = this.collection
    let data: {}

    if (patch && attributes && !this.isNew) {
      data = attributes
    } else if (patch && !this.isNew) {
      data = this.changes
    } else {
      data = { ...currentAttributes, ...attributes }
    }

    let method: RestMethod

    if (this.isNew) {
      method = 'post'
    } else if (patch) {
      method = 'patch'
    } else {
      method = 'put'
    }

    try {
      const newData = await apiClient()[method](path || this.url(), data, otherOptions)

      if (!newData) return

      const changes = getChangesBetween(currentAttributes, this.attributes)

      action('save success', () => {
        this.set(newData)
        this.commitChanges()

        if (collection) collection.add([this])

        if (keepChanges) {
          this.set(applyPatchChanges(newData, changes))
        }
      })()

      return newData
    } catch (error) {
      action('save error', () => {
        this.set(currentAttributes)
      })()

      throw error
    }
  }

  /**
   * Requests the backend to delete it.
   */
  async delete({ data, path, ...otherOptions }: DestroyOptions = {}) {
    // NOTE: It looks like `this.constructor` has an invalid type
    // Check https://github.com/Microsoft/TypeScript/issues/3841 to
    // know more about it.
    const url = path || (this.constructor as any).staticUrl(this.id)
    const newData = await apiClient().del(url, data, otherOptions)

    return newData
  }

  /**
   * Destroys the resource on the client and
   * requests the backend to delete it there
   * too
   *
   * @deprecated Use delete instead
   */
  async destroy({ data, path, ...otherOptions }: DestroyOptions = {}) {
    const collection = this.collection

    if (this.isNew && collection) {
      collection.remove(this)

      return
    }

    if (this.isNew) return

    // It is important to compute the url before removing it if
    // the url depends on the collection url.
    const url = path || this.url()
    const newData = await apiClient().del(url, data, otherOptions)

    if (collection) collection.remove(this)

    return newData
  }

  /**
   * Call an RPC action for all those
   * non-REST endpoints that you may have in
   * your API.
   */
  rpc(endpoint: string | { rootUrl: string }, options?: {}): Promise<any> {
    const url = isObject(endpoint) ? endpoint.rootUrl : `${this.url()}/${endpoint}`

    return apiClient().post(url, options)
  }
}

const dontMergeArrays = (_oldArray: any[], newArray: any[]) => newArray

/**
 * Merges old attributes with new ones.
 * By default it doesn't merge arrays.
 */
const applyPatchChanges = <T>(oldAttributes: T, changes: T): T => {
  return deepmerge(oldAttributes, changes, {
    arrayMerge: dontMergeArrays,
  })
}

const getChangedAttributesBetween = <T extends Record<string, any>>(source: T, target: T) => {
  const keys = union(Object.keys(source), Object.keys(target))

  return keys.filter((key) => !isEqual(source[key], target[key]))
}

const getChangesBetween = <T extends Record<string, any>>(source: T, target: T) => {
  const changes: Record<string, any> = {}

  getChangedAttributesBetween(source, target).forEach((key) => {
    changes[key] =
      isPlainObject(source[key]) && isPlainObject(target[key])
        ? getChangesBetween(source[key], target[key])
        : target[key]
  })

  return changes
}

const replaceAttributes = <T extends Record<string, any>>(attributes: T, data: T) => {
  runInAction(() => {
    const keysToDelete = difference(Object.keys(data), Object.keys(attributes))
    keysToDelete.forEach((key) => delete attributes[key])
    Object.assign(attributes, data)
  })
}

export * from './types'

export { Collection, Model, apiClient }
